import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './FeedbackFormPage.module.scss';
import logoImage from '../../assets/img/dark_logo.png';
import { logAndCaptureError, captureBusinessLogicError } from '../../utils/sentryErrorCapture';
import FeedbackForm from './feedback-form/FeedbackForm';
import IconSubmitted from './assets/submitted.svg?react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
  FeedbackFormAnswer,
  FeedbackFormContent,
  FeedbackForm as FeedbackFormType,
  SurveyTypes
} from '../../types';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Spinner from '../../components/spinner/Spinner';
import { useAnalytics, Events } from '../../hooks/useAnalytics';
import { useIntercom } from 'react-use-intercom';
import IconOutline from 'src/assets/icons/infocircle.svg?react';
import Button from '@src/components/button/Button';
import { isUrlShortenApp } from '@src/constants';
import exitticket from './assets/exitticket.jpg';
import { Tooltip } from 'antd';
import AuthCode from 'react-auth-code-input';
import './customStyle.css';
import { useMediaQuery } from 'react-responsive';
import TranslationBadge from '@src/components/translation-provider/TranslationBadge';
import { logger } from '../../utils/logger';

const feedbackFormQuery = gql`
  query GetFeedbackForm($input: FeedbackLinkInput!) {
    getFeedbackFormByLink(input: $input) {
      id,
      lessonName,
      programName,
      formData,
      expirationAt,
      unitName,
      isCancelled,
      link,
      createdBy {
        id
        customer {
          name
        }
        schools {
          name
        }
      }
    }
  }
`;

type FeedbackFormQueryResponse = {
  getFeedbackFormByLink: FeedbackFormContent;
}

const feedbackFormCreateAnswerMutation = gql`
  mutation CreateAnswer($input: FeedbackFormContentAnswerInput!) {
    createFeedbackFormAnswer(input: $input) {
      id
    }
  }
`;

type FeedbackFormCreateAnswerResponse = {
  createFeedbackFormAnswer: FeedbackFormAnswer;
};

type Params = { id: string };

function FeedbackFormPage() {
  const { shutdown, hide } = useIntercom();
  const { id } = useParams<Params>();
  const { identify, segment } = useAnalytics();
  const history = useHistory();
  const { pathname } = useLocation();

  const [code, setCode] = useState(id || '')
  const [formId, setFormId] = useState('');
  const [isAU, setIsAU] = useState(false);
  
  const isMobile = useMediaQuery({ maxWidth: 480 });
  useEffect(() => {
    hide();
  }, [hide])

  useEffect(() => {
    shutdown();
  }, [shutdown])

  const [getFeedbackForm, { data, loading }] = useLazyQuery<FeedbackFormQueryResponse>(feedbackFormQuery, {
    onCompleted(res) {
      const d = res?.getFeedbackFormByLink;
      if (d) {
        try {
          logger.debug(`[FeedbackForm] Form found: ${d.id}`);
          setForm(JSON.parse(d.formData ?? '{}'));
          setFormId(d.id);
          setIsAU(d.createdBy.id?.startsWith("AU_") ?? false);
          setExpirationError(false);
          setInvalid(false);
        } catch (error) {
          logAndCaptureError(`[FeedbackForm] Error parsing form data: ${error}`, error, {
            component: 'FeedbackFormPage',
            action: 'parseFormData',
            formId: d.id
          });
          setInvalid(true);
        }
      } else {
        logger.warn(`[FeedbackForm] Form data is empty or undefined`);
        setInvalid(true);
      }
    },
    onError(err) {
      
      const isExpired = err?.message === 'This survey already expired.' || 
                        err?.message === 'Exceeded the maximum number of exit tickets.';
      const isNotfound = err?.message === 'The form not found.' || 
                         err?.message === 'Form cancelled.';

      if (isExpired) {
        setExpirationError(true);
      } else if (isNotfound) {
        setInvalid(true);
      } else {
        // Handle any other errors
        captureBusinessLogicError(err, {
          component: 'FeedbackFormPage',
          action: 'fetchForm',
          additionalData: { formId: id, errorMessage: err.message }
        });
        logger.error(`[FeedbackForm] Unexpected error: ${err.message}`);
        setInvalid(true);
      }
    }
  });

  useEffect(() => {
    if (id) {
      logger.debug(`[FeedbackForm] Fetching form with ID: ${id}`);
      getFeedbackForm({ variables: { input: { link: id } } });
    } else {
      logger.debug(`[FeedbackForm] No ID provided, showing form entry UI`);
    }
  }, [getFeedbackForm, id]);

  const formInfo = useMemo(() => data?.getFeedbackFormByLink, [data?.getFeedbackFormByLink]);
  
  // Safely parse formData with error handling
  const formData = useMemo(() => {
    try {
      return JSON.parse(formInfo?.formData ?? '{}');
    } catch (error) {
      logAndCaptureError(`[FeedbackForm] Error parsing formData: ${error}`, error, {
        component: 'FeedbackFormPage',
        action: 'parseFormData',
        formInfo: formInfo?.id
      });
      return {};
    }
  }, [formInfo]);
  
  const surveyType = useMemo(() => {
    let surveyTypeInt = Number(formData.surveyType);
    switch (surveyTypeInt) {
      case 1:
        return SurveyTypes.ExitTickets;
      case 2:
        return SurveyTypes.Research;
      case 3:
        return SurveyTypes.PreProgram;
      case 4:
        return SurveyTypes.StudentQuiz;
      case 5:
        return SurveyTypes.PreProgram; // AU_PRE_PROGRAM_QUIZ
      case 6:
        return SurveyTypes.PostProgram; // POST_PROGRAM_QUIZ
      case 7:
        return SurveyTypes.AbsentStudent; // ABSENT_STUDENT
      default:
        return SurveyTypes.ExitTickets; // Default to Exit Tickets
    }
  }, [formData])

  // Translation is now handled by the TranslationBadge component

  const [createForm] = useMutation<FeedbackFormCreateAnswerResponse>(feedbackFormCreateAnswerMutation, {
    onError(e) {
      logger.error(`[FeedbackForm] Error submitting form: ${e.message}`);
      
      const errorMessage = e?.graphQLErrors?.[0]?.message || e?.message;
      if (errorMessage?.includes('This survey already expired') || 
          errorMessage?.includes('Exceeded the maximum number of exit tickets') || 
          errorMessage?.includes('Form cancelled') || 
          errorMessage?.includes('The form not found')) {
        logger.warn(`[FeedbackForm] Form expired or invalid: ${formId}`);
        setExpirationError(true);
      } else {
        // Handle any other errors
        captureBusinessLogicError(e, {
          component: 'FeedbackFormPage',
          action: 'submitForm',
          additionalData: { formId, errorMessage }
        });
        logger.error(`[FeedbackForm] Unexpected error during submission: ${errorMessage}`);
        setExpirationError(true);
      }
    }
  });

  const [form, setForm] = useState<FeedbackFormType>();
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [expirationError, setExpirationError] = useState(false);
  const [invalid, setInvalid] = useState(false);

  const go = useCallback(() => {
    if (code) {
      logger.debug(`[FeedbackForm] Navigating with code: ${code}`);
      
      let targetPath;
      if (isUrlShortenApp) {
        targetPath = `/${code}`;
      } else if (pathname === '/ff') {
        targetPath = `/ff/${code}`;
      } else {
        targetPath = `/feedbackform/${code}`;
      }
      
      logger.debug(`[FeedbackForm] Redirecting to: ${targetPath}`);
      history.push(targetPath);
    } else {
      logger.warn(`[FeedbackForm] Cannot navigate - no code provided`);
    }
  }, [code, history, pathname]);

  const handleSubmit = useCallback(async (form: string) => {
    try {
      if (formId && form) {
        logger.debug(`[FeedbackForm] Submitting form: ${formId}`);
        setFormSubmitting(true);
        
        await createForm({
          variables: {
            input: {
              formId: formId,
              formData: form
            }
          }
        });
        
        if (formInfo?.createdBy?.id) {
          const user = formInfo.createdBy;
          if (!user.id) {
            logger.warn(`[FeedbackForm] Missing user ID for analytics`);
            return;
          }
          
          logger.debug(`[FeedbackForm] Tracking form submission for user: ${user.id}`);
          segment?.identify(user.id);
          identify(user.id, {
            email: user?.email,
          });
          
          const _d = {
            course_name: formInfo.programName,
            lesson_name: formInfo.lessonName,
            customer_name: undefined,
            school_name: undefined,
            unit_name: formInfo.unitName,
            survey_type: surveyType,
            teacher_id: formInfo?.createdBy?.id,
          };
          
          if (user.customer?.name) {
            _d.customer_name = user.customer.name as any;
          }
          
          if (user.schools?.[0]?.name) {
            _d.school_name = user.schools?.[0]?.name as any;
          }
          
          await segment?.track(Events.actions.surveySubmitted, _d);
          logger.debug(`[FeedbackForm] Form submitted successfully`);
          setSubmitted(true);
        } else {
          logger.warn(`[FeedbackForm] Missing creator info for form: ${formId}`);
          setSubmitted(true);
        }
      } else {
        logger.error(`[FeedbackForm] Cannot submit form - missing formId or form data`);
        setExpirationError(true);
      }
    } catch (e) {
      logAndCaptureError(`[FeedbackForm] Error in handleSubmit: ${e}`, e, {
        component: 'FeedbackFormPage',
        action: 'handleSubmit',
        formId
      });
      setExpirationError(true);
    } finally {
      setFormSubmitting(false);
    }
  }, [createForm, formId, formInfo?.createdBy, formInfo?.lessonName, formInfo?.programName, formInfo?.unitName, identify, segment, surveyType]);

  const handleOnChange = useCallback((res: string) => {
    setCode(res);
  }, []);

  const content = useMemo(() => {
    if (submitted) {
      return (
        <div className={styles.submitted}>
          <IconSubmitted />
          <div className={styles.submittedTitle}>Thank you</div>
          <div className={styles.submittedSubtitle}>{form?.finalCopy || 'Your answers have been sent to your teacher.'}</div>
        </div>
      );
    }

    if (!form || !formInfo) return null;

    return (
      <>
        <div className={styles.content}>
          <FeedbackForm loading={formSubmitting} form={form!} onSubmit={handleSubmit} isAU={isAU} />
        </div>
      </>
    );
  }, [submitted, form, formInfo, formSubmitting, isAU, handleSubmit]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.logo}>
        <img src={logoImage} alt="Parachute Logo" />
        {/* Translation Badge for survey page - hidden for Australian users */}
        {!isAU && <TranslationBadge type="navbar" />}
      </div>
      {loading ? <div className={styles.mainForm}><Spinner /> </div> :
        <>
          {(!id || (id && (expirationError || invalid))) ?
            <div className={styles.mainForm}>
              {expirationError ?
                <div className={styles.formErrorTitle}>This link has <strong>expired</strong> and is <strong>no longer valid.</strong></div> :
                <div className={styles.formTitle}>{'Please enter your unique 4 digit code:'}</div>}
              <div className={styles.formSubtitle}>{expirationError ?
                'Please ask your teacher to generate a new code and enter it below:' :
                'This code will be displayed on the Lesson screen'}
              </div>
              <AuthCode onChange={handleOnChange} inputClassName={!invalid ? "input" : "errorinput"} containerClassName="container" length={4} />
              {invalid && <div className={styles.invalidErrorTitle}>Code is invalid!</div>}
              <Button className={code.length < 4 ? styles.okButton : styles.okEnableButton} label={'CONTINUE'} onClick={go} disabled={code.length < 4} />
              <Tooltip
                placement='top'
                title={
                  <div className={styles.tipView}>
                    <div className={styles.tipText}>
                      <span>Your unique code will be displayed on the slide in the Lesson.</span>
                      <span>If you cannot see this slide, please ask the teacher to show it to you again.</span>
                    </div>
                    <img src={exitticket} className={styles.tooltipImage} alt={'exitticket'} />
                  </div>
                }
                overlayStyle={{ maxWidth: isMobile ? '100%' : '1059px' }}
                color='#FFFFFF'>
                <div className={styles.troubleView}>
                  <div className={styles.formSubtitle}>{'Trouble finding your code?'}</div>
                  <IconOutline className={styles.tooltip} width={26} height={26} style={{ marginLeft: '10px' }} />
                </div>
              </Tooltip>
            </div> : content
          }
        </>
      }
    </div>
  );
}

export default FeedbackFormPage;
